'use client'

import { useEffect } from 'react'

import { isEmailDrivenSession, reportEmailClickIdentify } from '@/lib/email-click/report-identify'
import { runWhenIdle } from '@/lib/run-when-idle'
import { useLogger } from '@/hooks/use-logger'
import { useLocale } from '@/i18n/locale'

/**
 * Reports an email-driven session so Blueshift can stitch the anonymous Snowplow
 * cookie to the known recipient of the email the visitor just clicked.
 * See wanderu/strategy#418 and wanderu/nexus#647.
 *
 * `bsft_uid` is only a signal that the session is email-driven. It is never
 * transmitted, and no email is ever sent — identity resolution happens on
 * Blueshift's side.
 */
export function EmailClickIdentify() {
    const locale = useLocale()
    const logger = useLogger({ module: 'email-click-identify' })

    useEffect(() => {
        // Guard first: almost every page load is not email-driven, and those should
        // cost a single query-string read and nothing more.
        if (!isEmailDrivenSession(window.location.search)) {
            return
        }

        // Deferred behind an idle callback: the tracker wait plus the POST must not
        // compete with landing-page paint (CWV). Deferring also gives the tracker
        // bootstrap (`afterInteractive`) time to define its command queue.
        const run = () => {
            void reportEmailClickIdentify({ locale }).then((outcome) => {
                if (outcome !== 'sent') {
                    logger.withMetadata({ outcome }).warn('email-click identify not sent')
                }
            })
        }

        return runWhenIdle(run)
    }, [locale, logger])

    return null
}
